import React from "react";
import {
  Box,
  Button,
  Chip,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import type { ClientUser } from "../api/clientManagement";

interface UserRowProps {
  user: ClientUser;
  statusLabel: string;
  statusTone: "success" | "muted" | "warning";
  onOpenEdit: () => void;
}

export const UserRow: React.FC<UserRowProps> = ({
  user,
  statusLabel,
  statusTone,
  onOpenEdit,
}) => {
  const consentStatus = user.consent_status
    ? user.consent_status
    : user.requires_consent
      ? "required"
      : "completed";

  const consentLabel =
    consentStatus === "withdrawn"
      ? "Withdrawn"
      : consentStatus === "completed"
        ? "Completed"
        : "Required";

  const consentColor: "error" | "success" | "warning" =
    consentStatus === "withdrawn"
      ? "error"
      : consentStatus === "completed"
        ? "success"
        : "warning";

  const wearStatus = user.wear_jeopardy_status ?? null;
  const wearLabel = wearStatus ? (WEAR_STATUS_LABELS[wearStatus] ?? wearStatus) : null;

  const enabledServices = SERVICE_LABELS.filter(
    (s) => user.enabled_services?.[s.key],
  );

  const departments = user.departments ?? [];
  const jobRoles = user.job_roles ?? [];

  return (
    <TableRow hover onClick={onOpenEdit} sx={{ cursor: "pointer" }}>
      {/* User */}
      <TableCell sx={{ minWidth: 180 }}>
        <Stack spacing={0.25}>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Typography variant="body2" fontWeight={600}>
              {user.first_name} {user.last_name}
            </Typography>
            {user.is_admin && (
              <Chip
                label="Admin"
                size="small"
                color="warning"
                variant="filled"
                sx={{ fontSize: 10, height: 18, fontWeight: 700, "& .MuiChip-label": { px: 0.75 } }}
              />
            )}
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {user.email}
          </Typography>
        </Stack>
      </TableCell>

      {/* Departments */}
      <TableCell sx={{ minWidth: 140 }}>
        <ChipList
          items={departments.map((d) => d.name)}
          maxVisible={2}
          color="default"
        />
      </TableCell>

      {/* Job roles */}
      <TableCell sx={{ minWidth: 140 }}>
        <ChipList
          items={jobRoles.map((r) => r.name)}
          maxVisible={2}
          color="default"
        />
      </TableCell>

      {/* Services */}
      <TableCell sx={{ minWidth: 120 }}>
        {enabledServices.length ? (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {enabledServices.map((s) => (
              <Chip
                key={s.key}
                label={s.shortLabel}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ fontSize: 11, height: 20 }}
              />
            ))}
          </Stack>
        ) : (
          <Typography variant="caption" color="text.disabled">
            None
          </Typography>
        )}
      </TableCell>

      {/* First connected */}
      <TableCell sx={{ minWidth: 120 }}>
        {user.first_connected_at ? (
          <Typography variant="caption">
            {new Date(user.first_connected_at).toLocaleDateString()}
          </Typography>
        ) : (
          <Typography variant="caption" color="text.disabled">
            —
          </Typography>
        )}
      </TableCell>

      {/* Wear jeopardy */}
      <TableCell sx={{ minWidth: 130 }}>
        {wearLabel ? (
          <Stack spacing={0.5}>
            <Chip
              label={wearLabel}
              size="small"
              sx={{
                fontSize: 11,
                height: 22,
                color: WEAR_STATUS_COLORS[wearStatus!]?.color,
                backgroundColor: WEAR_STATUS_COLORS[wearStatus!]?.backgroundColor,
                fontWeight: 600,
                "& .MuiChip-label": { px: 1 },
              }}
            />
            {user.wear_jeopardy_status_reason && (
              <Tooltip title={user.wear_jeopardy_status_reason} placement="top">
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    maxWidth: 140,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    display: "block",
                    cursor: "default",
                  }}
                >
                  {user.wear_jeopardy_status_reason}
                </Typography>
              </Tooltip>
            )}
          </Stack>
        ) : (
          <Typography variant="caption" color="text.disabled">
            —
          </Typography>
        )}
      </TableCell>

      {/* Consent */}
      <TableCell>
        <Chip
          label={consentLabel}
          size="small"
          color={consentColor}
          variant="outlined"
          sx={{ fontSize: 11, height: 22, fontWeight: 600 }}
        />
      </TableCell>

      {/* Status */}
      <TableCell>
        <Chip
          label={statusLabel}
          size="small"
          color={
            statusTone === "success"
              ? "success"
              : statusTone === "warning"
                ? "warning"
                : "default"
          }
          variant={statusTone === "muted" ? "outlined" : "filled"}
          sx={{ fontSize: 11, height: 22, fontWeight: 600 }}
        />
      </TableCell>

      {/* Actions */}
      <TableCell align="right">
        <Button
          variant="outlined"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onOpenEdit();
          }}
          sx={{ fontSize: 12, py: 0.25 }}
        >
          Edit
        </Button>
      </TableCell>
    </TableRow>
  );
};

// ── Helpers ────────────────────────────────────────────────────────────────────

const ChipList: React.FC<{
  items: string[];
  maxVisible: number;
  color?: "default" | "primary" | "secondary";
}> = ({ items, maxVisible, color = "default" }) => {
  if (!items.length) {
    return (
      <Typography variant="caption" color="text.disabled">
        —
      </Typography>
    );
  }

  const visible = items.slice(0, maxVisible);
  const overflow = items.length - maxVisible;

  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {visible.map((item) => (
        <Chip
          key={item}
          label={item}
          size="small"
          color={color}
          variant="outlined"
          sx={{ fontSize: 11, height: 20 }}
        />
      ))}
      {overflow > 0 && (
        <Tooltip title={items.slice(maxVisible).join(", ")} placement="top">
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              px: 0.75,
              height: 20,
              borderRadius: 10,
              fontSize: 11,
              fontWeight: 600,
              color: "text.secondary",
              bgcolor: "action.selected",
              cursor: "default",
            }}
          >
            +{overflow}
          </Box>
        </Tooltip>
      )}
    </Stack>
  );
};

// ── Constants ──────────────────────────────────────────────────────────────────

const SERVICE_LABELS = [
  { key: "learning_locker" as const, label: "Learning Locker", shortLabel: "LL" },
  { key: "occupational_health" as const, label: "Occupational Health", shortLabel: "OH" },
  { key: "wearables" as const, label: "Wearables", shortLabel: "Wear" },
  { key: "akea_life" as const, label: "Akea Life", shortLabel: "Akea" },
];

const WEAR_STATUS_LABELS: Record<string, string> = {
  not_worn: "Not worn",
  intermittent_wear: "Intermittent",
  not_connected: "Not connected",
  recently_connected: "Recent",
  wearing_consistently: "Consistent",
};

const WEAR_STATUS_COLORS: Record<string, { color: string; backgroundColor: string }> = {
  not_worn: { color: "#991b1b", backgroundColor: "#fee2e2" },
  intermittent_wear: { color: "#92400e", backgroundColor: "#fef3c7" },
  not_connected: { color: "#374151", backgroundColor: "#e5e7eb" },
  recently_connected: { color: "#1e3a8a", backgroundColor: "#dbeafe" },
  wearing_consistently: { color: "#166534", backgroundColor: "#dcfce7" },
};

